<script lang="ts">
  // Lock banner rendered above an editor when the staged-edits store
  // holds edits of a *different* value type from what this editor
  // would touch. Per the governance principle (spec §1.4) a proposal
  // is one change-line of one value type — the reviewer accountable
  // for that type is the one who signs it off. The store enforces
  // this by throwing ValueTypeMismatchError on a cross-type stage
  // call; this banner is the operator-facing surface that prevents
  // the throw from ever firing, by disabling the editor's inputs
  // and offering a clean way out (Discard).
  //
  // The catalogue is a single JSON file per (jurisdiction, product),
  // so this lock is not a file-system constraint — amounts, fees,
  // and purposes all live inside the same file. The lock is the
  // change-line invariant talking.

  import { discardStaged } from '../lib/staged-edits.svelte';

  interface Props {
    // The value type the current draft is holding (i.e. blocking).
    currentValueType: string;
    // The value type the operator is trying to edit (the surface
    // this banner is rendered above).
    attemptedValueType: string;
  }

  const { currentValueType, attemptedValueType }: Props = $props();

  function label(v: string): string {
    if (v === 'rates') return 'rate cube';
    if (v === 'amounts') return 'loan amount range';
    if (v === 'fees') return 'fee schedule';
    if (v === 'purposes') return 'loan purposes';
    return v;
  }

  function discardAndContinue() {
    discardStaged();
  }
</script>

<div class="value-type-lock banner banner-warn" role="status">
  <p>
    <strong>{label(currentValueType).charAt(0).toUpperCase() + label(currentValueType).slice(1)} edits in progress.</strong>
    Each proposal covers one type of change so the reviewer accountable for
    that type is the one who signs it off. You cannot mix {label(currentValueType)}
    and {label(attemptedValueType)} edits in the same draft.
  </p>
  <p class="muted">
    Submit the in-progress {label(currentValueType)} edits via the existing
    <em>Submit for review</em> button, or discard them here to start editing
    {label(attemptedValueType)}.
  </p>
  <div class="value-type-lock__actions">
    <button class="btn" type="button" onclick={discardAndContinue}>
      Discard {label(currentValueType)} edits
    </button>
  </div>
</div>

<style>
  .value-type-lock {
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
    margin-bottom: var(--space-3);
  }
  .value-type-lock p { margin: 0; }
  .value-type-lock__actions {
    display: flex;
    justify-content: flex-end;
    gap: var(--space-2);
  }
</style>
